/**
 * Shared types of the Arvo API and console (server, platform UI and facade).
 * Spec: `docs/development/plans/arvo-console.md`.
 */

/**
 * An Eevo version by its id (`eevo-1.2`), in both decide modes (Default and Direct). Which versions
 * exist and which one is current is data, the server's version registry (PLT-8131,
 * `common/arvo/eevo-versions.ts`), so no list of them is code. Which checkpoint answers a version is
 * a routing matter (`engine/routing.ts`), not a model the caller picks.
 */
export type ArvoModelId = string;

/**
 * The id the docs, examples, snippets and our own callers use (PLT-7961): it always names the current
 * Eevo-1 version, so none of them needs an edit when a new version is promoted. A request with it, or
 * without `model`, is run, billed and stored as the version that answered it.
 */
export const ARVO_PUBLIC_MODEL_ID = 'eevo-1';

/** `arvo-1`, the id from before the Eevo name (PLT-7564): taken as `eevo-1`. */
export const LEGACY_ARVO_MODEL_ID = 'arvo-1';

/** An Eevo-1 version id: `eevo-1.2`, `eevo-1.13`. */
const EEVO_1_VERSION_ID = /^eevo-1\.(\d{1,3})$/;

/**
 * Whether `model` is an Eevo-1 model as stored or sent: a version id, the unversioned `eevo-1` of
 * decisions made before the versioned ids, or `arvo-1` on rows the rename has not reached (a
 * development env's). The model filters show all of them as one model.
 */
export function isEevo1ModelId(model: string): boolean {
  return model === ARVO_PUBLIC_MODEL_ID || model === LEGACY_ARVO_MODEL_ID || EEVO_1_VERSION_ID.test(model);
}

/**
 * Models no longer served whose decisions the log still keeps: `arvo-1-plus` listed each option's
 * consequences with Sonnet and sized them with Flash, which Default mode does with the caller's own
 * consequences; `arvo-1-flash` was the smaller variant before Arvo became one model.
 */
const RETIRED_MODEL_LABELS: Partial<Record<string, string>> = {
  'arvo-1-plus': 'Eevo-1 Plus',
  'arvo-1-flash': 'Eevo-1 Flash',
};

/**
 * The decision log's and insights' model filter options: one per model people see. Eevo-1's versions
 * are one model (`eevo-1`), whichever version answered.
 */
export const ARVO_FILTER_MODEL_IDS = [ARVO_PUBLIC_MODEL_ID, 'arvo-1-flash', 'arvo-1-plus'] as const;
export type ArvoFilterModelId = (typeof ARVO_FILTER_MODEL_IDS)[number];

/** The filter option a stored or requested model falls under, or null for one no filter shows. */
export function arvoFilterModelOf(model: unknown): ArvoFilterModelId | null {
  if (typeof model !== 'string') return null;
  if (isEevo1ModelId(model)) return ARVO_PUBLIC_MODEL_ID;
  return (ARVO_FILTER_MODEL_IDS as readonly string[]).includes(model) ? (model as ArvoFilterModelId) : null;
}

/**
 * The display name of a stored decision's model (PLT-7864): the version that answered, so `eevo-1.2`
 * decisions say Eevo-1.2 and `eevo-1.1` ones keep Eevo-1.1, and rows stored with the unversioned
 * `eevo-1` (before PLT-7801) say Eevo-1. A retired model keeps its name; an unknown id is shown raw.
 */
export function arvoModelLabel(model: string): string {
  const version = EEVO_1_VERSION_ID.exec(model);
  if (version) return `Eevo-1.${version[1]}`;
  if (model === ARVO_PUBLIC_MODEL_ID || model === LEGACY_ARVO_MODEL_ID) return 'Eevo-1';
  return RETIRED_MODEL_LABELS[model] ?? model;
}

/**
 * The default request rate limits (an organization's entitlements can raise them): API requests per
 * key per minute, and API plus playground requests per organization per minute. Over either, 429.
 */
export const ARVO_DEFAULT_RATE_LIMITS = { perKeyPerMinute: 600, perOrganizationPerMinute: 1_200 } as const;

/** `unlimited` keeps the content with no expiry (`expiresAt` null): the retention sweep never reaches it. */
export const ARVO_RETENTIONS = ['none', '30d', '90d', '1y', 'unlimited'] as const;
export type ArvoRetention = (typeof ARVO_RETENTIONS)[number];

/** The EU region: requests asked to be served there run on Eevo's EU nodes only, and are billed at the EU price. */
export const ARVO_EU_REGION = 'eu';

/**
 * Where an organization's requests are served (Data controls, PLT-7651):
 * - `global`: in the global region, unless a request asks for the EU (the `eu.eevo.io` base URL).
 * - `eu-default`: the API's base URL decides (`eevo.io` global, `eu.eevo.io` EU); the playground
 *   and connectors run in the EU.
 * - `eu-only`: always in the EU; a request on another region's host is refused.
 * See `resolveArvoRegion`.
 */
export const ARVO_REGION_POLICIES = ['global', 'eu-default', 'eu-only'] as const;
export type ArvoRegionPolicy = (typeof ARVO_REGION_POLICIES)[number];

export const ARVO_OPTION_IDS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const;
export type ArvoOptionId = (typeof ARVO_OPTION_IDS)[number];

/**
 * Eevo-1's context window: tokens per question, the shared state plus one question. A request over
 * it is refused, never truncated. The character limits below keep text inside it: 1.9 characters a
 * token, the low tail of Eevo-1's tokenizer on real texts (median about 4), so text that passes
 * them almost always fits, and the engine's own refusal is the backstop (PLT-7961). Every Eevo-1
 * version must have these limits (`EEVO_1_FAMILY_LIMITS`); one that does not is a code change.
 */
export const ARVO_CONTEXT_TOKENS = 73_728;

/** A SystemOne state: (73,728 − 4,096 tokens for the question and template) × 1.9 characters. */
export const ARVO_MAX_STATE_CHARS = 130_000;

export const ARVO_LIMITS = {
  /** The state's budget less 32,000 characters for 8 options and 8 stakeholders at their limits. */
  maxContextChars: 95_000,
  minOptions: 2,
  maxOptions: 8,
  maxOptionChars: 2_000,
  minStakeholders: 1,
  maxStakeholders: 8,
  maxStakeholderIdChars: 40,
  maxStakeholderChars: 2_000,
} as const;

/** Stakeholder ids: letters, digits, `_` and `-`. */
export const ARVO_STAKEHOLDER_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

/** The default id of the stakeholder at `index` (0-based) when the caller gives none: `S1`, `S2`, … */
export function defaultStakeholderId(index: number): string {
  return `S${index + 1}`;
}

// ---------------------------------------------------------------------------
// Request metadata (both endpoints, the MCP tools and the playground)
// ---------------------------------------------------------------------------

export const ARVO_CONVERSATION_PLATFORMS = ['slack', 'teams', 'google_chat', 'other'] as const;
export type ArvoConversationPlatform = (typeof ARVO_CONVERSATION_PLATFORMS)[number];

export const ARVO_CONVERSATION_PLATFORM_LABELS: Record<ArvoConversationPlatform, string> = {
  slack: 'Slack',
  teams: 'Microsoft Teams',
  google_chat: 'Google Chat',
  other: 'Chat',
};

export const ARVO_ISSUE_TRACKERS = ['linear', 'jira', 'github', 'other'] as const;
export type ArvoIssueTracker = (typeof ARVO_ISSUE_TRACKERS)[number];

export const ARVO_ISSUE_TRACKER_LABELS: Record<ArvoIssueTracker, string> = {
  linear: 'Linear',
  jira: 'Jira',
  github: 'GitHub',
  other: 'Issue',
};

export const ARVO_PR_PLATFORMS = ['github', 'gitlab', 'bitbucket', 'azure_devops', 'other'] as const;
export type ArvoPrPlatform = (typeof ARVO_PR_PLATFORMS)[number];

export const ARVO_PR_PLATFORM_LABELS: Record<ArvoPrPlatform, string> = {
  github: 'GitHub',
  gitlab: 'GitLab',
  bitbucket: 'Bitbucket',
  azure_devops: 'Azure DevOps',
  other: 'PR',
};

/** The chat conversation a request came from. */
export interface ArvoConversation {
  platform: ArvoConversationPlatform;
  /**
   * The channel's name, e.g. `#data-team` on Slack. A Teams channel lives inside a team, so for
   * Teams it is `Team / Channel`.
   */
  channel?: string;
  /**
   * Link to the thread or message, e.g. a Slack permalink or a Teams
   * `https://teams.microsoft.com/l/message/{channelId}/{messageId}?…&parentMessageId=…`.
   */
  thread_url?: string;
}

/** The issue or ticket a request is about. */
export interface ArvoIssueRef {
  tracker: ArvoIssueTracker;
  url: string;
  /** The tracker's key, e.g. `PLT-7003`. */
  key?: string;
  title?: string;
}

/** The pull (merge) request a request is about. */
export interface ArvoPullRequestRef {
  platform: ArvoPrPlatform;
  url: string;
  /** e.g. `uprightproject/upright`. */
  repo?: string;
  number?: number;
  title?: string;
}

/**
 * Where a request came from and for whom: callers (typically bots acting for a person) attach it so
 * the decision log can show and filter by it. It is not sent to the model and does not change the
 * answer. It is personal data, so it follows the key's retention exactly like the request and
 * response: not stored under `none`, deleted at `expiresAt`. Unknown keys are rejected.
 */
export interface ArvoDecisionMetadata {
  conversation?: ArvoConversation;
  issue?: ArvoIssueRef;
  pull_request?: ArvoPullRequestRef;
  /** The person the caller acts for. */
  on_behalf_of?: { name?: string; email?: string };
  /** Other related links. */
  links?: Array<{ url: string; label?: string }>;
}

export const ARVO_METADATA_KEYS: ReadonlyArray<keyof ArvoDecisionMetadata> = [
  'conversation',
  'issue',
  'pull_request',
  'on_behalf_of',
  'links',
];

export const ARVO_METADATA_LIMITS = {
  /** URLs must be http(s). */
  maxUrlChars: 2_000,
  maxChannelChars: 200,
  maxIssueKeyChars: 100,
  maxIssueTitleChars: 300,
  maxRepoChars: 200,
  maxPullRequestTitleChars: 300,
  maxNameChars: 200,
  maxEmailChars: 320,
  maxLinks: 10,
  maxLinkLabelChars: 200,
} as const;

// ---------------------------------------------------------------------------
// Native API: POST /api/arvo/v1/decisions (both modes), GET /api/arvo/v1/decisions/{id}
// ---------------------------------------------------------------------------

/**
 * One stakeholder whose interests count. `id` is optional (default `S1`, `S2`, … by position); ids
 * must be unique within the request, at most 40 characters of `[A-Za-z0-9_-]`.
 */
export interface ArvoStakeholder {
  id?: string;
  description: string;
  values?: string;
}

/**
 * A Direct mode decision: the model runs once per stakeholder with the same context and options
 * (1–8 stakeholders). The response gives each stakeholder's result side by side; there is no
 * combined verdict. Default mode is `ArvoExplicitDecisionRequest`.
 */
export interface ArvoDecideRequest {
  model: ArvoModelId;
  mode: 'direct';
  context: string;
  options: Array<{ id: ArvoOptionId; description: string }>;
  stakeholders: ArvoStakeholder[];
  /** Optional: where the request came from and for whom (decision log only; not sent to the model). */
  metadata?: ArvoDecisionMetadata;
  /**
   * Optional: the decision (`dec_…`) this request revises, which must be the latest revision of its
   * chain. Give it with `revision_note`. Like `metadata`, neither is sent to the model.
   */
  revises?: string;
  /** What the caller had missed in the decision it revises; required with `revises`. */
  revision_note?: ArvoRevisionNote;
  /**
   * Optional: whether everyone in the organization sees this decision (true) or only you (false).
   * Without it, the API key's setting, or your own for the Playground and Connectors. A revision
   * always follows its chain, so this cannot be given with `revises`. Not sent to the model.
   */
  share_with_org?: boolean;
}

/** Set on an option-order averaged answer: the most orderings a question was asked in. */
export interface ArvoOrderAveragingMeta {
  orderings: number;
}

// ---------------------------------------------------------------------------
// Sharing within an organization (PLT-7551): a decision is shared with the organization, or only its
// owner sees it: the person who made it, or for an API key's decision the key's creator (and the key).
// Admins do not see others' decisions that are not shared; usage and billing count every decision.
// ---------------------------------------------------------------------------

/** Where a decision came from. */
export const ARVO_DECISION_SOURCES = ['api', 'mcp', 'playground', 'connector', 'email', 'slack', 'web'] as const;
export type ArvoDecisionSource = (typeof ARVO_DECISION_SOURCES)[number];

/**
 * Sources whose decisions are never used to improve Eevo, whatever the account's data-use setting:
 * Slack's app rules forbid training on Slack data (`upserve/modules/eevo-slack-bot`).
 */
export const ARVO_SOURCES_NEVER_TRAINED_ON: ReadonlySet<ArvoDecisionSource> = new Set<ArvoDecisionSource>(['slack']);

/** The data-use flag a new decision row gets: the account's setting, unless the source rules it out. */
export function arvoDataUseAllowed(accountAllows: boolean, source: ArvoDecisionSource): boolean {
  return accountAllows && !ARVO_SOURCES_NEVER_TRAINED_ON.has(source);
}

/** The decision log's sharing filter: decisions you own, or those others shared with you. */
export const ARVO_SHARING_FILTERS = ['mine', 'shared_by_others'] as const;
export type ArvoSharingFilter = (typeof ARVO_SHARING_FILTERS)[number];

/** PATCH /api/arvo/v1/decisions/{id} (API key) */
export interface ArvoUpdateDecisionRequest {
  shared_with_org: boolean;
}

/** PATCH /api/arvo/decisions/{id}/sharing (console) */
export interface ArvoDecisionSharingRequest {
  sharedWithOrg: boolean;
}

/** The sharing of the console's account: the organization's default and the signed-in person's own choice. */
export interface ArvoSharingSettings {
  /** Members' Playground and Connector decisions are shared unless they choose otherwise. */
  organizationDefault: boolean;
  /** The person's own choice; null follows `organizationDefault`. */
  mine: boolean | null;
}

/** PUT /api/arvo/settings/sharing: the signed-in person's own choice (null: the organization's default). */
export interface ArvoUpdateMySharingRequest {
  shareDecisions: boolean | null;
}

// ---------------------------------------------------------------------------
// Revisions: a decide request may revise an earlier decision (`revises`). The decisions form one
// linear chain from the first (the root): each revision names the latest one before it. A revision
// is a full, independently billed decide call; the chain only links them for the decision log.
// ---------------------------------------------------------------------------

/** What the caller had left out or got wrong in the revised decision. */
export const ARVO_MISSED_KINDS = [
  'context',
  'consequence',
  'stakeholder',
  'option',
  'size_or_probability',
  'other',
] as const;
export type ArvoMissedKind = (typeof ARVO_MISSED_KINDS)[number];

export const ARVO_MISSED_KIND_LABELS: Record<ArvoMissedKind, string> = {
  context: 'Context',
  consequence: 'Consequence',
  stakeholder: 'Stakeholder',
  option: 'Option',
  size_or_probability: 'Size or probability',
  other: 'Other',
};

export const ARVO_REVISION_LIMITS = { maxMissed: 20, maxMissedChars: 1_000, maxReasonChars: 2_000 } as const;

/**
 * Why a decision was revised. The kinds are kept for statistics whatever the retention; `reason` and
 * the descriptions are content and follow the decision's retention.
 */
export interface ArvoRevisionNote {
  /** What in the earlier result showed something was missing. */
  reason?: string;
  /** At least one; every item needs a description. */
  missed: Array<{ kind: ArvoMissedKind; description: string }>;
}

/** Where a decide result sits in its revision chain (every decide response carries it). */
export interface ArvoLineage {
  /** The chain's first decision; this decision's own id when it revises nothing. */
  root_id: string;
  /** 1 for the root, 2 for its first revision, … */
  number: number;
  /** The decision this one revises; null for the root. */
  previous_id: string | null;
}

export interface ArvoUsage {
  input_tokens: number;
  output_tokens: number;
}

/** One stakeholder's answer. */
export interface ArvoStakeholderResult {
  stakeholder: { id: string; description: string };
  best: ArvoOptionId;
  confidence: number;
  probabilities: Partial<Record<ArvoOptionId, number>>;
}

/** Whether the stakeholders' best options coincide. Deliberately no combined verdict. */
export interface ArvoAgreement {
  all_same: boolean;
  /** Stakeholder id → that stakeholder's best option. */
  best_by_stakeholder: Record<string, ArvoOptionId>;
}

export interface ArvoDecideResponse {
  id: string;
  model: ArvoModelId;
  /** One result per stakeholder, in request order. */
  results: ArvoStakeholderResult[];
  agreement: ArvoAgreement;
  /** Summed over the stakeholders' runs. */
  usage: ArvoUsage;
  /** Summed over the stakeholders' runs. */
  cost_usd: number;
  /** The decision's place in its revision chain; always set by the API, absent on responses stored before revisions. */
  lineage?: ArvoLineage;
  /** Only on an option-order averaged answer. */
  meta?: ArvoOrderAveragingMeta;
}

// ---------------------------------------------------------------------------
// Decide modes: Default, explicit consequence sizing (the caller lists each option's consequences;
// Arvo sizes them pairwise, models their overlap and computes the expected value per option and
// stakeholder), the recommended mode; and Direct (the model answers which option is best in a single
// call), for high volume and custom tasks. Engine spec: uptrain
// `docs/notes/consequence-mode-engine-spec.md`. The code calls Default mode "explicit" after its
// mechanism.
// ---------------------------------------------------------------------------

/** The wire values of `mode`; a request without one runs in Default mode. */
export const ARVO_DECIDE_MODES = ['default', 'direct'] as const;
export type ArvoDecideMode = (typeof ARVO_DECIDE_MODES)[number];
export const ARVO_MODE_DIRECT: ArvoDecideMode = 'direct';
/** Default mode: explicit consequence sizing. */
export const ARVO_MODE_EXPLICIT: ArvoDecideMode = 'default';

export const ARVO_DECIDE_MODE_LABELS: Record<ArvoDecideMode, string> = {
  default: 'Default',
  direct: 'Direct',
};

/**
 * The modes the console's Usage tab breaks spend down by: the two decide modes, and Free-form (the
 * SystemOne-compatible endpoint, logged with endpoint `systemone` and the column default `direct`).
 */
export const ARVO_USAGE_MODES = ['default', 'direct', 'freeform'] as const;
export type ArvoUsageMode = (typeof ARVO_USAGE_MODES)[number];

export const ARVO_USAGE_MODE_LABELS: Record<ArvoUsageMode, string> = {
  ...ARVO_DECIDE_MODE_LABELS,
  freeform: 'Free-form (SystemOne)',
};

export function arvoUsageMode(row: {
  endpoint: 'decide' | 'systemone' | 'playground';
  mode?: ArvoDecideMode;
}): ArvoUsageMode {
  return row.endpoint === 'systemone' ? 'freeform' : (row.mode ?? ARVO_MODE_DIRECT);
}

/**
 * `overlap` of an explicit response. Arvo always takes overlap into account (`correct`: the shared
 * part of overlapping consequences is counted once), and requests no longer choose. `report` (overlaps
 * returned, not applied) and `off` (not assessed) remain readable for any stored response that has them.
 */
export const ARVO_OVERLAP_MODES = ['correct', 'report', 'off'] as const;
export type ArvoOverlapMode = (typeof ARVO_OVERLAP_MODES)[number];

export const ARVO_EXPLICIT_LIMITS = {
  minConsequences: 2,
  maxConsequences: 40,
  maxConsequencesPerStakeholder: 20,
  maxConsequenceChars: 1_000,
  maxConsequenceIdChars: 40,
  maxLabelChars: 60,
  /** Overlap pairs of one stakeholder's run (each asked in both orders). */
  maxOverlapPairs: 120,
  /**
   * Model calls of a whole request, all stakeholders together: ≈ 65 s on one idle 27B replica (H200).
   */
  maxRecordsPerRequest: 450,
} as const;

export type ArvoValence = 'benefit' | 'harm';

/** One consequence of an option, as the caller lists it. */
export interface ArvoExplicitConsequenceInput {
  /** Unique; default `c1`, `c2`, … by position. At most 40 characters of `[A-Za-z0-9_-]`. */
  id?: string;
  option: ArvoOptionId;
  /** One concrete effect: what happens, to whom, how much. Shown in the log, and on the decision's public page if it is published. */
  description: string;
  /** A short name for the views (at most 60 characters); not sent to the model. */
  label?: string;
  /** The stakeholder ids it affects; default every stakeholder. */
  stakeholders?: string[];
  /** For every stakeholder, or per stakeholder id (one entry for each of its stakeholders). */
  valence: ArvoValence | Record<string, ArvoValence>;
}

/**
 * An explicit decision as the engine runs it and the decision log stores it: one flat list of
 * consequences, each naming its option. Callers send `ArvoExplicitDecisionRequest`, with each
 * option's consequences listed under it (`flattenExplicitRequest` turns it into this).
 */
export interface ArvoExplicitDecideRequest extends Omit<ArvoDecideRequest, 'mode'> {
  mode: 'default';
  consequences: ArvoExplicitConsequenceInput[];
  /** uint32; default 20261006. The same request and seed give the same result. */
  seed?: number;
  /**
   * Answer 202 with the decision id at once instead of waiting for the result, which is then
   * fetched from `GET /v1/decisions/{id}`. Without it the request waits up to about 50 s first.
   */
  async?: boolean;
}

/** A consequence as listed under its option in `POST /v1/decisions`. */
export type ArvoOptionConsequence = Omit<ArvoExplicitConsequenceInput, 'option'>;

/** An option of an explicit decision, with its consequences (at least one). */
export interface ArvoExplicitOption {
  id: ArvoOptionId;
  description: string;
  consequences: ArvoOptionConsequence[];
}

/**
 * `POST /v1/decisions` in Default mode (explicit consequence sizing): the Direct mode request with each
 * option's consequences listed under the option.
 */
export interface ArvoExplicitDecisionRequest extends Omit<ArvoExplicitDecideRequest, 'options' | 'consequences'> {
  options: ArvoExplicitOption[];
}

/** The body of `POST /v1/decisions`: `mode` says which. */
export type ArvoDecisionRequest = ArvoDecideRequest | ArvoExplicitDecisionRequest;

/** A value with its Monte Carlo p10–p90 band; `mean` / `value` is the point estimate, not a draw average. */
export interface ArvoBand {
  mean: number;
  p10: number;
  p90: number;
}

export type ArvoOverlapType = 'none' | 'restatement' | 'containment' | 'causal' | 'mirror';

export interface ArvoExplicitConsequenceResult {
  id: string;
  option: ArvoOptionId;
  description: string;
  label?: string;
  /** For this stakeholder. */
  valence: ArvoValence;
  /** On the stakeholder's own scale (largest sized consequence = 1); null when it could not be sized. */
  size: { value: number; p10: number; p90: number } | null;
  /** 1 − the share already counted in another consequence; null when unsized. */
  counted_share: number | null;
  /** The pairs it overlaps with by τ or more, largest first. */
  overlaps_with: Array<{ with: string; share: number }>;
  /**
   * The overlap that set its discount (the counted share is a maximum over pairs): with whom, their
   * relation, the pair's share, the part of this consequence's expected effect removed, and for a
   * mirror the probability that this one is the absence side. Null when nothing is discounted.
   */
  discounted_by: {
    with: string;
    type: ArvoOverlapType;
    overlap: number;
    share: number;
    absence_p: number | null;
  } | null;
}

export interface ArvoExplicitOverlap {
  /** In listing order. */
  a: string;
  b: string;
  /** Share of the smaller consequence's effect already counted in the other (0–1). */
  share: number;
  /** P(share > 20%). */
  p: number;
  type: ArvoOverlapType;
  type_p: number;
  /** `within`: same option and valence; `mirror`: other options, opposite valence. */
  kind: 'within' | 'mirror';
  /** Mirror only: the side that only states the other's absence, and how sure that is. */
  absence_side?: string;
  absence_p?: number;
  /** Per side, the probability that it is the absence side (0.5 / 0.5 when unknown). */
  absence: Record<string, number>;
}

export interface ArvoExplicitStakeholderResult extends ArvoStakeholderResult {
  /** P(highest EV) per option over the Monte Carlo draws; `probabilities` holds the same. */
  p_best: Partial<Record<ArvoOptionId, number>>;
  /** The same before overlap is taken into account (every consequence counted in full), and its argmax. */
  p_best_uncorrected: Partial<Record<ArvoOptionId, number>>;
  best_uncorrected: ArvoOptionId;
  ev: Partial<Record<ArvoOptionId, ArvoBand>>;
  ev_uncorrected: Partial<Record<ArvoOptionId, ArvoBand>>;
  consequences: ArvoExplicitConsequenceResult[];
  overlaps: ArvoExplicitOverlap[];
  /** Σ e × share / Σ e (0 unless overlap is `correct`). */
  removed_share: number;
  mc: { draws: number; solved: number };
  records: { ratio: number; overlap: number };
}

export interface ArvoExplicitDecideResponse extends Omit<ArvoDecideResponse, 'results' | 'usage' | 'meta'> {
  mode: 'default';
  overlap: ArvoOverlapMode;
  seed: number;
  results: ArvoExplicitStakeholderResult[];
  warnings: string[];
  /**
   * Priced per decision, not per token: `billed_decisions` is the standard decisions it counts as
   * (its price is a multiple of the decision price), and the records the model calls it made.
   * Responses stored before per-decision pricing have `input_tokens` and `output_tokens` instead.
   */
  usage: { billed_decisions: number; ratio_records: number; overlap_records: number };
  /** The console's result view and its PNG (API key or session); null when nothing is stored. */
  links: { view_url: string; png_url: string } | null;
  meta: {
    tau: number;
    draws: number;
    ratio_spread_scale: number;
    overlap_model: string;
    /**
     * The overlap correction rules the result was computed with: whether mirror pairs discount
     * (`mirror`) and whether the kept rule applies (`kept`). Absent on results from before
     * v2.2.513 (6 Oct 2026), which had mirrors on and the kept rule off.
     */
    correction?: { mirror: boolean; kept: boolean };
  };
  notes: string;
}

// ---------------------------------------------------------------------------
// SystemOne-compatible API: POST /api/arvo/v1/systemone
// ---------------------------------------------------------------------------

export type SystemOneQuestion =
  | { type: 'choice'; instructions?: string; criteria: Record<string, string> }
  | { type: 'score'; instructions?: string; criteria: string[] }
  | { type: 'noul'; instructions?: string };

export interface SystemOneRequest {
  model: ArvoModelId;
  state: string | Record<string, unknown>;
  questions: Record<string, SystemOneQuestion>;
  /**
   * Optional, an Arvo extension of the SystemOne format (other SystemOne servers ignore it): where the
   * request came from and for whom, for the decision log. Not sent to the model.
   */
  metadata?: ArvoDecisionMetadata;
  /** Optional, an Eevo extension like `metadata`: as `share_with_org` of a decide request. */
  share_with_org?: boolean;
}

/**
 * Answer shapes exactly as the Clef release's `systemone_answer` returns them
 * (`joint_schema_model.py` in Cloudflare/clef-flash): a score's levels are the
 * string indices "0", "1", … of its criteria, with `legend` mapping them back.
 */
export type SystemOneAnswer =
  | { type: 'choice'; choice: string; confidence: number; probabilities: Record<string, number> }
  | {
      type: 'score';
      score: number;
      confidence: number;
      legend: Record<string, string>;
      probabilities: Record<string, number>;
    }
  | { type: 'noul'; noul: number };

export interface SystemOneResponse {
  /** The Arvo request id (`dec_…`, as in the decision log). Added by the Arvo API; absent on the raw serving response. */
  id?: string;
  model: string;
  answers: Record<string, SystemOneAnswer>;
  usage: ArvoUsage;
  /** Only on an option-order averaged answer (an Eevo extension). */
  meta?: ArvoOrderAveragingMeta;
}

// ---------------------------------------------------------------------------
// Chosen decision reports: POST /api/arvo/v1/decisions/{id}/chosen (API key) and
// POST /api/arvo/decisions/{id}/chosen (console)
// ---------------------------------------------------------------------------

export const ARVO_CHOSEN_DECIDERS = ['agent', 'human'] as const;
export type ArvoChosenDecider = (typeof ARVO_CHOSEN_DECIDERS)[number];

export const ARVO_CHOSEN_LIMITS = { maxNoteChars: 2_000 } as const;

/**
 * What the caller reports was done after a decision. One report covers the whole decision (all its
 * stakeholders): the chosen option is compared with each stakeholder's best. Any key of the
 * decision's organization may report; reporting again overwrites. `followed`, `chosen_option` and
 * `decided_by` are kept for statistics like the other usage columns; `note` and `reported_by` are
 * personal data and follow the decision's retention like its metadata.
 */
export interface ArvoChosenDecision {
  /**
   * In a report: "followed the stakeholders' common best option". When the stakeholders disagree
   * there is none, so give `chosen_option` instead.
   */
  followed: boolean;
  /** The option that was carried out, or `none` when none was. */
  chosen_option?: ArvoOptionId | 'none';
  decided_by?: ArvoChosenDecider;
  note?: string;
  /** Set by the server. */
  reported_at: string;
  reported_by?: { name?: string; email?: string };
}

/**
 * The chosen decision report's body (one per decision, covering every stakeholder). With `chosen_option`,
 * each stakeholder's `followed` is `chosen_option === that stakeholder's best`. `followed` alone means
 * the stakeholders' common best was (not) followed: a 400 when they disagree (give `chosen_option`).
 * Both given and contradicting is a 400, as is neither.
 */
export type ArvoReportChosenRequest = Omit<ArvoChosenDecision, 'reported_at' | 'followed'> & { followed?: boolean };

/** The chosen decision as the decision log lists it (kept for statistics, whatever the retention). */
export interface ArvoChosenSummary {
  /** Followed for at least one stakeholder. */
  followed: boolean;
  /** Followed for every stakeholder. */
  followedAll: boolean;
  /** Per stakeholder id, whether the chosen option was that stakeholder's best; null for SystemOne decisions. */
  followedBy: Record<string, boolean> | null;
  chosenOption: ArvoOptionId | 'none' | null;
  decidedBy: ArvoChosenDecider | null;
  reportedAt: string;
}

/** The chosen decision with its retention-governed part; `note` and `reportedBy` are null when not stored. */
export interface ArvoChosenDetail extends ArvoChosenSummary {
  note: string | null;
  reportedBy: { name?: string; email?: string } | null;
}

// ---------------------------------------------------------------------------
// Assessments: POST /api/arvo/v1/decisions/{id}/assessments (API key) and
// POST /api/arvo/decisions/{id}/assessments (console)
// ---------------------------------------------------------------------------

/**
 * Whether the assessor (an agent, or a person in the console) agrees with a decision's result.
 * Separate from the chosen decision: the chosen decision is what was done, an assessment is a judgement of Arvo's
 * answer, recorded before or without knowing the chosen decision. A decision can have several (one per
 * assessor and occasion); each is kept, none overwrites another.
 */
export const ARVO_ASSESSMENT_OVERALL = ['agree', 'partly', 'disagree'] as const;
export type ArvoAssessmentOverall = (typeof ARVO_ASSESSMENT_OVERALL)[number];

export const ARVO_ASSESSMENT_OVERALL_LABELS: Record<ArvoAssessmentOverall, string> = {
  agree: 'Agrees',
  partly: 'Partly agrees',
  disagree: 'Disagrees',
};

export const ARVO_BEST_CORRECT = ['yes', 'no', 'unsure'] as const;
export type ArvoBestCorrect = (typeof ARVO_BEST_CORRECT)[number];

export const ARVO_ASSESSOR_KINDS = ['agent', 'human'] as const;
export type ArvoAssessorKind = (typeof ARVO_ASSESSOR_KINDS)[number];

export const ARVO_ASSESSMENT_LIMITS = {
  maxReasonChars: 2_000,
  maxNoteChars: 2_000,
  maxAgentChars: 100,
  maxModelChars: 100,
} as const;

/** One stakeholder's verdict in an assessment. */
export interface ArvoStakeholderVerdict {
  stakeholder: string;
  /**
   * Whether Arvo's best option for this stakeholder (in Explicit mode, the one with the highest
   * expected value) is the right one, in the assessor's judgement.
   */
  best_correct: ArvoBestCorrect;
  /** The option the assessor would choose for this stakeholder (`none`: none of them). */
  would_choose?: ArvoOptionId | 'none';
  /** The assessor's confidence in this verdict, 0–1. */
  confidence?: number;
  /** Why; required when `best_correct` is `no`. Content: follows the decision's retention. */
  reason?: string;
}

/** The body of an assessment. */
export interface ArvoAssessRequest {
  overall: ArvoAssessmentOverall;
  /** One verdict per stakeholder of the decision, each exactly once. */
  stakeholders: ArvoStakeholderVerdict[];
  /** Who assessed: `kind` defaults to `agent` over the API and `human` in the console. */
  assessor?: { kind?: ArvoAssessorKind; agent?: string; model?: string };
  /** The person the assessor acts for (or the person assessing). Personal data: follows the retention. */
  on_behalf_of?: { name?: string; email?: string };
  note?: string;
}

/** An assessment as stored and returned; the content fields are null once not stored. */
export interface ArvoAssessment {
  id: string;
  decisionId: string;
  createdAt: string;
  overall: ArvoAssessmentOverall;
  assessor: { kind: ArvoAssessorKind; agent?: string; model?: string };
  /** Kept whatever the retention; `reason` is null when not stored. */
  stakeholders: Array<Omit<ArvoStakeholderVerdict, 'reason'> & { reason: string | null }>;
  note: string | null;
  onBehalfOf: { name?: string; email?: string } | null;
}

/** The latest assessment of a decision as the log lists it. */
export interface ArvoAssessmentSummary {
  overall: ArvoAssessmentOverall;
  assessorKind: ArvoAssessorKind;
  createdAt: string;
  /** How many assessments the decision has. */
  count: number;
}

export interface ArvoApiError {
  error: {
    type:
      | 'invalid_request'
      | 'authentication'
      | 'insufficient_credit'
      | 'model_unavailable'
      | 'model_warming'
      | 'free_capacity_busy'
      /** The region a request asked for (e.g. the EU) has no healthy endpoint (PLT-7617). */
      | 'region_unavailable'
      | 'too_many_overlap_pairs'
      | 'too_many_records'
      | 'rate_limited'
      | 'revision_conflict'
      | 'server_error';
    message: string;
  };
}

// ---------------------------------------------------------------------------
// Console API (session auth; organization from the user)
// ---------------------------------------------------------------------------

/**
 * POST /api/arvo/drafts (public): the body is `ArvoDraft['payload']` itself (`context`, `options`,
 * `stakeholders`, optional `model` at the top level, no wrapper) → `{id, expiresAt}`.
 * GET /api/arvo/drafts/{id} (signed in) → ArvoDraft.
 */
export interface ArvoDraft {
  id: string;
  payload: Omit<ArvoDecideRequest, 'model' | 'mode' | 'metadata'> & { model?: ArvoModelId };
  expiresAt: string;
}

/**
 * A personal account is one person's own: nobody can be invited to it, and it can be converted to
 * an organization. An organization account has members, invited by its admins. Both are an
 * organization (`OrganizationType.ARVO`) underneath, so credit, keys and the log work the same.
 */
export const ARVO_ACCOUNT_KINDS = ['personal', 'organization'] as const;
export type ArvoAccountKind = (typeof ARVO_ACCOUNT_KINDS)[number];

/**
 * The console sends the account it acts for in this header (the organization id). Without it the
 * server picks the default account: the internal one for team users, else the personal account,
 * else the oldest organization.
 */
export const ARVO_ACCOUNT_HEADER = 'x-arvo-account';

/** How long an invitation to an Arvo organization stays valid. Re-inviting the email renews it. */
export const ARVO_INVITATION_VALID_DAYS = 21;

/** One of the Arvo accounts the signed-in person belongs to (GET /api/arvo/accounts). */
export interface ArvoAccountListItem {
  organizationId: number;
  /** The organization's name; for a personal account the UI shows "Personal" instead. */
  organizationName: string;
  kind: ArvoAccountKind;
  role: 'admin' | 'member';
  internal: boolean;
}

/** GET /api/arvo/accounts */
export interface ArvoAccountsResponse {
  accounts: ArvoAccountListItem[];
  /** The account the server uses when the console names none. Null when the person has no account. */
  defaultOrganizationId: number | null;
  /** False once the person has a personal account: one each. */
  canCreatePersonal: boolean;
  /** False for Upright team users, who use the internal account. */
  canCreateAccounts: boolean;
  /**
   * Whether the next account the person creates gets the free decision quota (`ARVO_FREE_QUOTA`):
   * once per person, on their first account, so false after that.
   */
  getsFreeQuota: boolean;
}

/** The free decision quota of an account and what of it is used (PLT-7343). */
export interface ArvoFreeQuota {
  /** Decisions in any mode, revisions included but for free Default mode revisions. */
  decisions: { used: number; limit: number };
  /** Free revisions of each free Default mode decision. */
  revisionsPerExplicitDecision: number;
}

/** GET /api/arvo/account */
export interface ArvoAccountSummary {
  organizationId: number;
  organizationName: string;
  kind: ArvoAccountKind;
  role: 'admin' | 'member';
  /** True for the Upright-internal account (team users; never charged). */
  internal: boolean;
  /**
   * True once the account has bought credit, and for the internal account: only then can retention
   * be "Not stored" (`none`), on an API key or as the connector retention.
   */
  billed: boolean;
  balanceUsd: number;
  /** The account's free decision quota; null when it has none (it is not the person's first account). */
  freeQuota?: ArvoFreeQuota | null;
  autoRecharge: { enabled: boolean; thresholdUsd: number; amountUsd: number };
  dataUseAllowed: boolean;
  defaultRetention: ArvoRetention;
  /** Where the organization's requests are served (`ARVO_REGION_POLICIES`). */
  regionPolicy: ArvoRegionPolicy;
  /**
   * Whether the organization may run requests in the EU (the `eu.eevo.io` base URL and the EU region
   * settings): it has bought credit or has credit on its balance. Not while on free decisions alone.
   */
  euAvailable: boolean;
  /** The model and whether it is served now. */
  models: Array<{ id: ArvoModelId; label: string; available: boolean; note?: string }>;
  /** True once a card is saved by a credit purchase; auto-recharge can only be enabled then. */
  paymentMethodSaved?: boolean;
  /**
   * Why the last auto-recharge failed (a declined card, a missing billing address, a temporary
   * error), shown on the Billing page; null when the last attempt succeeded or none was made.
   */
  autoRechargeError?: string | null;
  /**
   * The org's user groups, for the Members page: invite into `memberGroupId` (and `adminGroupId` for
   * admins) with the platform's user-group invitation routes (`/api/user-groups/{groupId}/…`).
   * Null for the internal account, whose members are the Upright team.
   */
  groups?: { organizationGroupId: number; adminGroupId: number | null; memberGroupId: number | null } | null;
  /** Who in the organization sees new decisions. */
  sharing?: ArvoSharingSettings;
  /**
   * The account was created through an invite link and gets its invite credit at its first decision,
   * which it has not run yet. False when the inviter has no invite left, since the credit would not come.
   */
  pendingInviteCredit?: boolean;
}

/**
 * POST /api/arvo/account — create a personal or an organization account for the signed-in person.
 * `organizationName` is required for an organization and ignored for a personal account.
 */
export interface ArvoCreateAccountRequest {
  kind: ArvoAccountKind;
  organizationName?: string;
  dataUseAllowed: boolean;
  /** The code of the invite link the person arrived with (`referrals.ts`), when they had one. */
  inviteCode?: string;
}

/** POST /api/arvo/account/convert — turn the current personal account into an organization. */
export interface ArvoConvertAccountRequest {
  organizationName: string;
}

/**
 * The API playground, session-authenticated: POST /api/arvo/playground/decide (body as
 * `ArvoDecisionRequest`) and POST /api/arvo/playground/systemone (body `SystemOneRequest`, answer
 * `SystemOneResponse`), both on the connector retention.
 */

export interface ArvoApiKey {
  id: string;
  name: string;
  prefix: string;
  retention: ArvoRetention;
  createdAt: string;
  createdBy: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** Whether the key's decisions are shared with the organization. */
  shareDecisions?: boolean;
  /** Whether the signed-in person created the key: only they change `shareDecisions`. */
  createdByYou?: boolean;
}

/** POST /api/arvo/keys → ArvoApiKey & { secret } (secret shown once); PATCH /api/arvo/keys/{id}; DELETE revokes. */
export interface ArvoCreateApiKeyRequest {
  name: string;
  retention: ArvoRetention;
  /** Default true. */
  shareDecisions?: boolean;
}

/** How much of a decision's `context` the log lists: enough for a card's headline and its expanded excerpt. */
export const ARVO_CONTEXT_PREVIEW_CHARS = 600;

export const ARVO_DECISION_STATUSES = ['ok', 'error', 'running'] as const;
export type ArvoDecisionStatus = (typeof ARVO_DECISION_STATUSES)[number];

export interface ArvoRunProgress {
  /** Model calls answered so far. */
  done: number;
  /** Model calls the request needs. */
  total: number;
}

/** A decision's status as the console names it: `Running (n of N)` while it runs. */
export function arvoDecisionStatusLabel(row: {
  status: ArvoDecisionStatus;
  progress?: ArvoRunProgress | null;
}): string {
  if (row.status === 'ok') return 'OK';
  if (row.status === 'error') return 'Failed';
  return row.progress ? `Running (${row.progress.done} of ${row.progress.total})` : 'Running';
}

/**
 * `POST /v1/decisions` (Default mode) answers 202 with this when the decision is still running at
 * the end of the first response (or at once with `async: true`): fetch it by `id` from
 * `GET /v1/decisions/{id}` until `status` is `ok` (the decide answer is in `response`) or `error`.
 */
export interface ArvoDecisionRunning {
  id: string;
  model: ArvoModelId;
  mode: 'default';
  status: 'running';
  progress: ArvoRunProgress;
  /** The `GET /v1/decisions/{id}` URL to poll. */
  poll_url: string;
  /** Seconds to wait before the next poll. */
  retry_after: number;
  lineage?: ArvoLineage;
}

export interface ArvoDecisionRow {
  id: string;
  createdAt: string;
  /** An `ArvoModelId`, or a retired model's id on older rows (`arvoModelLabel` names both). */
  model: string;
  endpoint: 'decide' | 'systemone' | 'playground';
  /** `running`: a Default mode decision still being sized (it answered 202); `ok` or `error` once done. */
  status: ArvoDecisionStatus;
  /** Why the request failed, as the API answered it; null (or absent) on successful rows. */
  errorMessage?: string | null;
  /** While `running`: model calls answered of those planned; null otherwise. */
  progress?: ArvoRunProgress | null;
  /** The stakeholders' common best option; null when they disagree (or on errors and SystemOne). */
  best: ArvoOptionId | null;
  /** The best option's probability when there is one stakeholder; null with several (see the response). */
  confidence: number | null;
  numOptions: number | null;
  /** Decide only: how many stakeholders the model ran for; null for SystemOne and errors. */
  numStakeholders: number | null;
  /** Decide only: stakeholder id → best option (kept like `best`, whatever the retention). */
  bestByStakeholder: Record<string, ArvoOptionId> | null;
  /** Direct mode and SystemOne, billed per token; null for Default mode, which is priced per decision. */
  inputTokens: number | null;
  /**
   * Default mode: the standard decisions it counts as, which its price is a multiple of; null for
   * Direct mode, SystemOne and decisions from before per-decision pricing.
   */
  billedDecisions?: number | null;
  /** What the account was charged: 0 for a decision of the free quota. */
  costUsd: number;
  /** Paid for by the account's free decision quota. */
  free?: boolean;
  latencyMs: number;
  apiKey: { id: string; name: string; prefix: string } | null;
  user: { id: number; name: string } | null;
  /** False when retention is `none` or the content has expired. */
  contentStored: boolean;
  /** The request's metadata; null when none was sent, retention is `none`, or it has expired with the content. */
  metadata: ArvoDecisionMetadata | null;
  /** The reported chosen decision; null until one is reported. */
  chosen: ArvoChosenSummary | null;
  /** The decide mode; `direct` (also for SystemOne rows) when absent. */
  mode?: ArvoDecideMode;
  /** Option id → its description, from the stored request; null (or absent) when the content is not stored. */
  optionDescriptions?: Partial<Record<ArvoOptionId, string>> | null;
  /**
   * The start of the stored request's `context` (at most `ARVO_CONTEXT_PREVIEW_CHARS`, ending in "…"
   * when the context is longer), what the decision was about; null (or absent) when the content is
   * not stored or the request had none.
   */
  contextPreview?: string | null;
  /** Its place in a revision chain; null (or absent) when it neither revises nor has been revised. */
  revision?: ArvoRevisionPosition | null;
  /** The latest assessment; null (or absent) when there is none. */
  assessment?: ArvoAssessmentSummary | null;
  /** Whether everyone in the organization sees it; when false, only its owner. */
  sharedWithOrg?: boolean;
  /** Whether the caller may change `sharedWithOrg`: they made it, or it was made with their key. */
  canChangeSharing?: boolean;
  /** Its public page (`/d/{token}`) while it is published; null (or absent) otherwise. */
  publication?: ArvoDecisionPublication | null;
  /** Whether the caller may publish it: they own it (as for `canChangeSharing`) and its result is stored. */
  canPublish?: boolean;
  /** Where it came from; null (or absent) on older decisions where it cannot be told. */
  source?: ArvoDecisionSource | null;
  /** A Connector decision: the app connected (`Claude`, `ChatGPT`, …), when known. */
  connectorApp?: string | null;
}

export interface ArvoRevisionPosition {
  rootId: string;
  /** 1 for the root. */
  number: number;
  previousId: string | null;
  /** The chain's latest revision number: `number` of `latestNumber`. */
  latestNumber: number;
  /** What this revision says was missed in the previous one (kept whatever the retention); null for the root. */
  missedKinds: ArvoMissedKind[] | null;
}

/** One decision of a revision chain, as the decision detail lists the chain. */
export interface ArvoRevisionSummary {
  id: string;
  number: number;
  createdAt: string;
  mode: ArvoDecideMode;
  best: ArvoOptionId | null;
  bestByStakeholder: Record<string, ArvoOptionId> | null;
  missedKinds: ArvoMissedKind[] | null;
  /** Null when not stored (retention) or for the root. */
  note: ArvoRevisionNote | null;
}

/**
 * `GET /api/arvo/decisions/range-counts` and `GET /api/arvo/insights/stakeholders/range-counts`:
 * the page's count from each requested `since` to now, in the order requested.
 */
export interface ArvoRangeCounts {
  counts: number[];
}

/**
 * GET /api/arvo/decisions?from&to&apiKeyId&model&status&agreement&stakeholder&platform&channel&person&issue&pullRequest&link&hasMetadata&chosen&revisions&revised&assessment&cursor&limit
 *
 * Revision filters: `revisions=latest` only the latest decision of each revision chain; `revised`
 * true/false decisions in / not in a chain of two or more. `assessment` agree | partly | disagree |
 * none: the latest assessment's overall verdict, or no assessment.
 *
 * Metadata filters: `platform` the conversation's platform and `channel` its exact channel (within
 * `platform` when given); `person` a case-insensitive substring of `on_behalf_of.name` or `.email`;
 * `issue` a case-insensitive substring of the issue's key, title or url; `pullRequest` one of the
 * pull request's repo, `#number`, title or url; `link` a case-insensitive substring of any URL
 * (`conversation.thread_url`, `issue.url`, `pull_request.url`, `links[].url`);
 * `hasMetadata` true/false; `chosen` followed_all | followed_some | not_followed | not_reported
 * (a partition: `followed_some` is followed for some stakeholders but not all); `agreement` agree |
 * disagree (decide rows whose stakeholders do / do not share a best option); `stakeholder` one or
 * more comma-separated stakeholder ids (decide rows whose `bestByStakeholder` has every one of them).
 */
export interface ArvoDecisionListResponse {
  rows: ArvoDecisionRow[];
  nextCursor: string | null;
  /** Daily counts over [from, to] with the same filters except the date: the volume histogram. */
  volume: Array<{ date: string; count: number }>;
}

export const ARVO_CHOSEN_FILTERS = ['followed_all', 'followed_some', 'not_followed', 'not_reported'] as const;
export type ArvoChosenFilter = (typeof ARVO_CHOSEN_FILTERS)[number];

export const ARVO_ASSESSMENT_FILTERS = [...ARVO_ASSESSMENT_OVERALL, 'none'] as const;
export type ArvoAssessmentFilter = (typeof ARVO_ASSESSMENT_FILTERS)[number];

export const ARVO_AGREEMENT_FILTERS = ['agree', 'disagree'] as const;
export type ArvoAgreementFilter = (typeof ARVO_AGREEMENT_FILTERS)[number];

/** GET /api/arvo/decisions/filter-options?from&to: metadata values in the range, most frequent first, at most 100 each. */
export interface ArvoDecisionFilterOptions {
  channels: Array<{ platform: ArvoConversationPlatform; channel: string; count: number }>;
  people: Array<{ name?: string; email?: string; count: number }>;
  /** The trackers of the issues in the range, most frequent first. */
  issueTrackers?: ArvoIssueTracker[];
}

/** The `stakeholder` filter of the decision log and how many ids it takes (comma-separated, all must be present). */
export const ARVO_STAKEHOLDER_FILTER_MAX_IDS = 8;

// ---------------------------------------------------------------------------
// Stakeholder insights: GET /api/arvo/insights/stakeholders?from&to&apiKeyId&model
// ---------------------------------------------------------------------------

/**
 * How often a stakeholder's best option was the one carried out: of the decisions with a reported
 * chosen decision (`reported`), those where it was (`followed`). `rate` is followed / reported, null when
 * nothing was reported (no rate, which is not a zero rate).
 */
export interface ArvoFollowRate {
  reported: number;
  followed: number;
  rate: number | null;
}

/** One stakeholder id across the organization's decisions in the range (ids aggregated exactly). */
export interface ArvoStakeholderInsight {
  id: string;
  /** The description of its most recent decision whose content is still stored; else the id. */
  label: string;
  /** Decisions (ok, decide) this stakeholder took part in. */
  decisions: number;
  /** Of them, decisions where the stakeholders' best options were not all the same. */
  inConflict: number;
  /** How often the option carried out was this stakeholder's best, over its decisions with a reported chosen decision. */
  prioritized: ArvoFollowRate;
  /** The same over its conflicts only: of its reported conflicts, those where its best was carried out. */
  wonConflicts: ArvoFollowRate;
}

/** Two stakeholders that took part in the same decisions, and how often their best options differed. */
export interface ArvoStakeholderPair {
  a: string;
  b: string;
  /** Decisions with both. */
  together: number;
  /** Of them, decisions where their best options differed. */
  disagree: number;
  rate: number;
}

export interface ArvoStakeholderInsights {
  totals: {
    /** Decide decisions (status ok) with at least one stakeholder. */
    decisions: number;
    /** Of them, decisions with two or more stakeholders. */
    multiStakeholder: number;
    /** Multi-stakeholder decisions whose stakeholders' best options were not all the same. */
    conflicts: number;
    /** conflicts / multiStakeholder; null with no multi-stakeholder decisions. */
    conflictRate: number | null;
    /** Decisions with a reported chosen decision. */
    chosenReported: number;
  };
  /** The 50 most frequent stakeholder ids, most frequent first. */
  stakeholders: ArvoStakeholderInsight[];
  /** Pairs that met in at least 3 decisions, most disagreements first; at most 30. */
  pairs: ArvoStakeholderPair[];
  /** Every UTC day of the range, zeros included. */
  series: Array<{ date: string; decisions: number; multiStakeholder: number; conflicts: number }>;
}

/** GET /api/arvo/decisions/{id} */
/**
 * A decision's public page (`/d/{token}`, `upserve/modules/arvo/decisions/publication.ts`): anyone with
 * the link reads the decision without signing in. The content is kept while the decision is published.
 */
export interface ArvoDecisionPublication {
  /** The page's address, on Eevo's host. */
  url: string;
  publishedAt: string;
  /** The person who published it; null when the API key the decision was made with did. */
  publishedBy: { id: number; name: string } | null;
}

/** The request of a published decision as its page shows it: the context, options and stakeholders, nothing else. */
export type ArvoPublicDecisionRequest = Pick<ArvoExplicitDecideRequest, 'context' | 'options' | 'stakeholders'>;

/**
 * `GET /api/arvo/public/decisions/{token}`: a published decision as anyone with the link sees it.
 * The response is the stored one without what names the account: usage, cost, the console links and
 * the lineage. No metadata (people, channels), no key, no person.
 */
export interface ArvoPublicDecision {
  id: string;
  createdAt: string;
  model: string;
  mode: ArvoDecideMode;
  /** The issue's title when the request named one, else the context's first sentence (`explicitTitle`). */
  title: string;
  request: ArvoPublicDecisionRequest;
  response:
    | Omit<ArvoExplicitDecideResponse, 'usage' | 'cost_usd' | 'links' | 'lineage'>
    | Omit<ArvoDecideResponse, 'usage' | 'cost_usd' | 'lineage' | 'meta'>;
  /** The result image of each stakeholder (`?stakeholder=`), for Default mode decisions; null otherwise. */
  imageUrl: string | null;
}

export interface ArvoDecisionDetail extends ArvoDecisionRow {
  chosen: ArvoChosenDetail | null;
  request: unknown | null;
  response: unknown | null;
  expiresAt: string | null;
  /** The whole revision chain, oldest first; empty when the decision is not in one. */
  revisions?: ArvoRevisionSummary[];
  /** Every assessment, newest first. */
  assessments?: ArvoAssessment[];
  /**
   * The console's GET answers with this set when the decision is in another of the person's Arvo
   * accounts than the one the console asked for, so the console can switch to it.
   */
  organizationId?: number;
}

/** GET /api/arvo/usage?from&to&groupBy=day|mode|apiKey */
export interface ArvoUsageResponse {
  totals: ArvoUsageFigures;
  series: Array<{ key: string; label: string } & ArvoUsageFigures>;
}

export interface ArvoUsageFigures {
  requests: number;
  /** Direct mode and SystemOne, billed per token. Default mode is priced per decision: `billedDecisions`. */
  inputTokens: number;
  /** Default mode: the standard decisions its successful decisions counted as. */
  billedDecisions: number;
  costUsd: number;
  /**
   * Decisions with a reported chosen decision; of them, followed for at least one stakeholder
   * (`chosenFollowed`) and for every stakeholder (`chosenFollowedAll`). Follow rates are these / reported.
   */
  chosenReported: number;
  chosenFollowed: number;
  chosenFollowedAll: number;
}

/** GET /api/arvo/billing/ledger */
export interface ArvoLedgerEntry {
  id: number;
  createdAt: string;
  /**
   * `grant`: credit from Upright, without payment; negative when taken back (`credit-grants.ts`).
   * `referral`: an invite reward (`referrals.ts`), granted credit like a `grant`.
   * `promo`: credit from a promo code (`promo-codes.ts`), granted credit like a `grant`.
   */
  kind:
    | 'signup_grant'
    | 'purchase'
    | 'auto_recharge'
    | 'usage'
    | 'refund'
    | 'adjustment'
    | 'grant'
    | 'referral'
    | 'promo';
  amountUsd: number;
  note: string | null;
  receiptUrl: string | null;
}

/** The ledger kinds that are payments, each with its Stripe receipt: what `GET /api/v1/receipts` lists. */
export const ARVO_RECEIPT_KINDS = ['purchase', 'auto_recharge', 'refund'] as const;

/** One payment of `GET /api/v1/receipts`, for an accounting agent: the Stripe receipt is the proof of payment. */
export interface ArvoReceipt {
  id: number;
  created_at: string;
  kind: (typeof ARVO_RECEIPT_KINDS)[number];
  /** The credit it added in USD, before tax (negative for a refund). */
  amount_usd: number;
  /** For a purchase, the amount paid with its tax, e.g. "Credit purchase ($50 + $12.75 tax)". */
  description: string | null;
  /** Stripe's hosted receipt; null when Stripe gave none. */
  receipt_url: string | null;
}

export interface ArvoReceiptList {
  receipts: ArvoReceipt[];
}

/** POST /api/arvo/billing/checkout {amountUsd} → {clientSecret} (embedded Stripe Checkout). */
export interface ArvoCheckoutResponse {
  clientSecret: string;
}

/** PUT /api/arvo/settings {dataUseAllowed?, defaultRetention?, regionPolicy?, autoRecharge?} → ArvoAccountSummary */
export interface ArvoSettingsUpdate {
  dataUseAllowed?: boolean;
  defaultRetention?: ArvoRetention;
  regionPolicy?: ArvoRegionPolicy;
  /** Whether members' Playground and Connector decisions are shared unless they choose otherwise. */
  shareDecisionsDefault?: boolean;
  autoRecharge?: { enabled: boolean; thresholdUsd: number; amountUsd: number };
}
